<template>
  <div class="arp-hub" data-tour="arp-hub">
    <b-loading :active="loading" :is-full-page="false" />

    <template v-if="!journey.active">
      <div class="arp-hub__header" data-tour="arp-hub-header">
        <div class="arp-hub__title-row">
          <h2 class="title is-5 mb-0">ARP's Cadastradas</h2>
          <FeatureTourHelp
            v-if="exibirAjudaTourArp"
            class="arp-hub__tour-help"
            :icon-only="true"
            label=""
            icon="help-circle-outline"
            icon-size="is-small"
            contexto="auction.arp"
            scope="hub"
            :feature-ctx="ctxTourArp()"
            @selecionar="reabrirTutorialArp"
          />
        </div>
        <button
          type="button"
          class="button is-primary"
          data-tour="arp-nova-ata"
          @click="abrirNovaAta"
        >
          <span>Nova Ata de Registro de Preços</span>
        </button>
      </div>

      <ArpCadastradasTable
        :atas="atasCadastradas"
        :interacao-desabilitada="loading"
        @excluir="onExcluir"
        @download="onDownload"
        @enviar-assinada="onEnviarAssinada"
        @editar="onEditar"
        @carregar-documento="carregarDocumentoAta"
        @excluir-documento="excluirDocumentoAta"
        @adicionar-parte="adicionarParteEnvolvida"
        @excluir-parte="excluirParteEnvolvida"
      />
    </template>

    <ArpWizard v-else @voltar-hub="onVoltarHub" />

    <b-modal
      ref="enviarAssinadaModal"
      has-modal-card
      :active.sync="modalAssinada"
      :can-cancel="enviandoAssinada ? false : ['escape', 'outside']"
      :on-cancel="fecharModalAssinada"
      @close="fecharModalAssinada"
    >
      <ArpEnviarAssinadaModal
        v-if="modalAssinada && ataAssinada"
        :ata="ataAssinada"
        :enviando="enviandoAssinada"
        @close="fecharModalAssinada"
        @enviar="confirmarEnviarAssinada"
      />
    </b-modal>

    <b-modal
      ref="justificativaModal"
      has-modal-card
      :active.sync="modalJustificativa"
      :can-cancel="loading ? false : ['escape', 'outside']"
      :on-cancel="fecharJustificativa"
      @justificate="enviarJustificativa"
      @close="fecharJustificativa"
    >
      <AppJustification
        v-if="modalJustificativa"
        ref="justificativaForm"
        :display-fail-button="false"
        :max-length="255"
        :title-text="tituloJustificativa"
        :success-text="textoSucessoJustificativa"
      />
    </b-modal>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { fileServer } from '@/utils/functions';
import AppJustification from '@/components/AppJustification';
import FeatureTourHelp from '@/components/FeatureTourHelp';
import featureTourMixin from '@/mixins/featureTour';
import { isTourActive } from '@/feature-tours';
import ArpWizard from './ArpWizard';
import ArpCadastradasTable from './lists/ArpCadastradasTable';
import ArpEnviarAssinadaModal from './components/ArpEnviarAssinadaModal';
import {
  TIPO_DOCUMENTO_ARP_PRINCIPAL,
  TIPO_PARTE_GERENCIADORA,
  getPncpDocumentUploadSizeErrorMessage,
  isAtaRascunho,
  isWithinPncpDocumentUploadLimit,
  inferirTipoJornadaArp,
  mapAtaParaDraftArp,
  obterArquivoPrincipalArp,
} from './helpers';

const mapAta = ata => {
  const docs = Array.isArray(ata.documentos) ? ata.documentos : [];
  const partes = Array.isArray(ata.partesEnvolvidas) ? ata.partesEnvolvidas : [];
  let statusAssinatura = ata.statusAssinatura || null;
  if (!statusAssinatura) {
    // PDF gerado (rascunho) aguarda assinatura; assinada só com dataAssinatura.
    statusAssinatura = ata.dataAssinatura ? 'assinada' : 'aguardando';
  }

  return {
    id: ata.id,
    numero: ata.numeroAtaRegistroPreco || ata.numero,
    ano: ata.anoAta || ata.ano,
    sequencialAta: ata.sequencialAta,
    dataAssinatura: ata.dataAssinatura,
    dataVigenciaInicio: ata.dataVigenciaInicio,
    dataVigenciaFim: ata.dataVigenciaFim,
    possibilidadeAdesao: ata.possibilidadeAdesao,
    documentos: docs,
    partesEnvolvidas: partes,
    fornecedorNome: ata.fornecedorNome || ata.razaoSocial || '—',
    fornecedorId: ata.fornecedorId || ata.ofertaCompraFornecedorId || null,
    pncpUrl: ata.pncpUrl || null,
    integrationStatus: ata.integrationStatus || null,
    pncpIntegrado: Boolean(ata.pncpIntegrado || ata.sequencialAta),
    estado: ata.estado || null,
    origem: ata.origem || null,
    statusAssinatura,
  };
};

export default {
  name: 'ArpHub',
  mixins: [featureTourMixin],
  components: {
    AppJustification,
    FeatureTourHelp,
    ArpWizard,
    ArpCadastradasTable,
    ArpEnviarAssinadaModal,
  },
  data() {
    return {
      loading: false,
      modalJustificativa: false,
      justificativaAcao: null,
      ataExcluirId: null,
      documentoExcluirPayload: null,
      parteExcluirPayload: null,
      modalAssinada: false,
      ataAssinada: null,
      enviandoAssinada: false,
    };
  },
  computed: {
    ...mapGetters({
      atas: 'obterOfertaCompraAtasPrecos',
      journey: 'obterArpJourney',
      ofertaCompra: 'obterOfertaCompra',
      canAccessArpTab: 'canAccessArpTab',
      logged: 'getLogged',
    }),
    atasCadastradas() {
      return (this.atas || []).map(mapAta);
    },
    tituloJustificativa() {
      if (this.justificativaAcao === 'exclusao-documento') return 'Excluir documento';
      if (this.justificativaAcao === 'exclusao-parte') return 'Excluir parte envolvida';
      if (this.justificativaAcao === 'exclusao') return 'Excluir ARP';
      return 'Justificativa';
    },
    textoSucessoJustificativa() {
      if (
        this.justificativaAcao === 'exclusao' ||
        this.justificativaAcao === 'exclusao-documento' ||
        this.justificativaAcao === 'exclusao-parte'
      ) {
        return 'Excluir';
      }
      return 'Continuar';
    },
    exibirAjudaTourArp() {
      return (
        !this.journey.active &&
        this.opcoesAjudaContexto('auction.arp', this.ctxTourArp(), { scope: 'hub' }).length > 0
      );
    },
  },
  async created() {
    await this.carregar();
    this.agendarFeaturesArp();
  },
  beforeDestroy() {
    this.cancelarArpJourney();
  },
  methods: {
    ...mapActions([
      'buscarOfertaCompraAtasPrecos',
      'removerOfertaCompraAtaPreco',
      'publicarOfertaCompraAtaPreco',
      'adicionarOfertaCompraAtaPrecoDocumento',
      'removerOfertaCompraAtaPrecoDocumento',
      'adicionarOfertaCompraAtaPrecoParteEnvolvida',
      'removerOfertaCompraAtaPrecoParteEnvolvida',
      'zerarAtasPrecosUpload',
      'iniciarArpJourney',
      'cancelarArpJourney',
    ]),
    ctxTourArp(extra = {}) {
      return {
        logged: this.logged,
        canAccessArp: this.canAccessArpTab,
        journeyActive: Boolean(this.journey?.active),
        ...extra,
      };
    },
    preparesTourArp() {
      return {
        'arp-jornadas': async () => {
          // Garante hub visível (lista + Nova Ata) antes dos passos com waitForElement.
          if (this.journey?.active) {
            this.cancelarArpJourney();
            await this.$nextTick();
          }
        },
      };
    },
    agendarFeaturesArp() {
      this.$nextTick(() => {
        this.tentarIniciarTourArp();
      });
    },
    async tentarIniciarTourArp() {
      if (!this.logged || !this.canAccessArpTab) return;
      if (isTourActive()) return;
      if (this.journey?.active) return;

      await this.rodarContextoFeatures('auction.arp', this.ctxTourArp(), {
        prepares: this.preparesTourArp(),
      });
    },
    reabrirTutorialArp(featureId) {
      if (!featureId) return;
      this.$nextTick(() => {
        this.reabrirFeature(featureId, this.ctxTourArp(), {
          prepares: this.preparesTourArp(),
        });
      });
    },
    async carregar() {
      const id = this.ofertaCompra?.id || this.$route.params.id;
      if (!id) return;
      this.loading = true;
      try {
        await this.buscarOfertaCompraAtasPrecos(id);
      } catch (e) {
        this.$alerta('Não foi possível carregar as ARPs', 'is-danger');
      } finally {
        this.loading = false;
      }
    },
    abrirNovaAta() {
      this.iniciarArpJourney({ type: null });
    },
    onVoltarHub() {
      this.cancelarArpJourney();
    },
    async onExcluir(ata) {
      if (!ata?.id) return;

      const rascunho = isAtaRascunho(ata);
      const confirmar = await this.$confirmacao({
        message: rascunho
          ? 'A exclusão remove o rascunho da ARP no sistema. Esta ação não pode ser desfeita. Deseja continuar?'
          : 'A exclusão remove a ata no PNCP e no sistema. Esta ação não pode ser desfeita. Deseja continuar?',
        cancelText: 'Cancelar',
        confirmText: 'Continuar',
        type: 'is-danger',
        hasIcon: true,
      });
      if (!confirmar) return;

      this.justificativaAcao = 'exclusao';
      this.ataExcluirId = ata.id;
      this.modalJustificativa = true;
    },
    onDownload(ata) {
      const arquivo = obterArquivoPrincipalArp(ata);
      if (arquivo) {
        window.open(fileServer(arquivo), '_blank');
        return;
      }
      this.$alerta('Documento da ARP não encontrado para download.', 'is-warning');
    },
    onEnviarAssinada(ata) {
      this.ataAssinada = ata;
      this.modalAssinada = true;
    },
    fecharModalAssinada() {
      if (this.enviandoAssinada) return;
      this.modalAssinada = false;
      this.ataAssinada = null;
    },
    async confirmarEnviarAssinada({ ataId, dataAssinatura, arquivo }) {
      const formData = new FormData();
      formData.append('dataAssinatura', dataAssinatura);
      formData.append('file', arquivo);

      this.enviandoAssinada = true;
      this.zerarAtasPrecosUpload();
      try {
        await this.publicarOfertaCompraAtaPreco({ ataId, formData });
        this.$alerta('Ata salva. A integração com o PNCP está em processamento.', 'is-success');
        this.modalAssinada = false;
        this.ataAssinada = null;
        if (this.$refs.enviarAssinadaModal) {
          this.$refs.enviarAssinadaModal.close();
        }
      } catch (e) {
        const msg =
          e?.response?.data?.error || e?.response?.data?.message || 'Erro ao enviar ARP assinada';
        this.$alerta(msg, 'is-danger');
      } finally {
        this.enviandoAssinada = false;
        this.zerarAtasPrecosUpload();
      }
    },
    onEditar(ata) {
      if (!ata?.id) return;
      const type = inferirTipoJornadaArp(ata);
      const rascunho = isAtaRascunho(ata);
      this.iniciarArpJourney({
        type,
        mode: 'edit',
        isRascunho: rascunho,
        draft: mapAtaParaDraftArp(ata),
      });
    },
    fecharJustificativa() {
      if (this.loading) return;
      this.modalJustificativa = false;
      this.justificativaAcao = null;
      this.ataExcluirId = null;
      this.documentoExcluirPayload = null;
      this.parteExcluirPayload = null;
    },
    resetJustificativaModalState() {
      const form = this.$refs.justificativaForm;
      if (!form) return;
      if (form.loading) {
        form.loading.fail = false;
        form.loading.success = false;
      }
      if (form.action) {
        form.action.fail = false;
        form.action.success = false;
      }
    },
    enviarJustificativa({ justification }) {
      const texto = String(justification || '').trim();

      if (texto.length < 10) {
        this.resetJustificativaModalState();
        this.$alerta('Justificativa deve ter no mínimo 10 caracteres', 'is-warning');
        this.modalJustificativa = true;
        return;
      }

      if (texto.length > 255) {
        this.resetJustificativaModalState();
        this.$alerta(
          'Justificativa deve ter no máximo 255 caracteres (exigência PNCP)',
          'is-warning',
        );
        this.modalJustificativa = true;
        return;
      }

      this.modalJustificativa = false;

      if (this.justificativaAcao === 'exclusao') {
        this.confirmarExcluir(texto);
        return;
      }
      if (this.justificativaAcao === 'exclusao-documento') {
        this.excluirDocumentoAtaConfirmada(texto);
        return;
      }
      if (this.justificativaAcao === 'exclusao-parte') {
        this.excluirParteEnvolvidaConfirmada(texto);
      }
    },
    async confirmarExcluir(justificativa) {
      try {
        await this.removerOfertaCompraAtaPreco({
          id: this.ataExcluirId,
          justificativa,
        });
        this.$alerta('ARP excluída', 'is-success');
        this.fecharJustificativa();
      } catch (e) {
        const msg = e?.response?.data?.error || e?.response?.data?.message || 'Erro ao excluir';
        this.$alerta(msg, 'is-danger');
      }
    },
    async carregarDocumentoAta({ ata, arquivo }) {
      if (!ata?.id || !arquivo) return;

      if (!ata.sequencialAta) {
        this.$alerta(
          'A ata ainda não possui sequencial no PNCP; conclua o envio antes de adicionar documentos.',
          'is-warning',
        );
        return;
      }

      if (!isWithinPncpDocumentUploadLimit(arquivo.size)) {
        this.$alerta(getPncpDocumentUploadSizeErrorMessage(), 'is-danger');
        return;
      }

      const formData = new FormData();
      formData.append('file', arquivo);

      this.loading = true;
      try {
        this.zerarAtasPrecosUpload();
        await this.adicionarOfertaCompraAtaPrecoDocumento({
          ataId: ata.id,
          formData,
        });
        this.$alerta(
          'Documento salvo. A integração com o PNCP está em processamento.',
          'is-success',
        );
      } catch (error) {
        const msg =
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          'Erro ao enviar documento da ata';
        this.$alerta(msg, 'is-danger');
      } finally {
        this.loading = false;
        this.zerarAtasPrecosUpload();
      }
    },
    async adicionarParteEnvolvida({
      ata,
      documento,
      tipoParticipante,
      codigoUnidadeCompradora,
      nomeUnidadeCompradora,
    }) {
      if (!ata?.id || !documento) return;

      if (!ata.sequencialAta) {
        this.$alerta(
          'A ata ainda não possui sequencial no PNCP; conclua o envio antes de incluir partes envolvidas.',
          'is-warning',
        );
        return;
      }

      this.loading = true;
      try {
        await this.adicionarOfertaCompraAtaPrecoParteEnvolvida({
          ataId: ata.id,
          dados: {
            documento,
            tipoParticipante,
            codigoUnidadeCompradora,
            nomeUnidadeCompradora,
          },
        });
        this.$alerta(
          'Parte envolvida salva. A integração com o PNCP está em processamento.',
          'is-success',
        );
      } catch (error) {
        const msg =
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          'Erro ao incluir parte envolvida';
        this.$alerta(msg, 'is-danger');
      } finally {
        this.loading = false;
      }
    },
    async excluirDocumentoAta({ ata, documento }) {
      if (!ata?.id || !documento?.id) return;

      if (Number(documento.tipoDocumentoId) === TIPO_DOCUMENTO_ARP_PRINCIPAL) {
        this.$alerta('O documento principal da ata não pode ser excluído.', 'is-warning');
        return;
      }

      if (!ata.sequencialAta) {
        this.$alerta(
          'A ata ainda não possui sequencial no PNCP; conclua o envio antes de excluir documentos.',
          'is-warning',
        );
        return;
      }

      const confirmar = await this.$confirmacao({
        message:
          'A exclusão remove o documento no PNCP e no sistema. Esta ação não pode ser desfeita. Deseja continuar?',
        cancelText: 'Cancelar',
        confirmText: 'Continuar',
        type: 'is-danger',
        hasIcon: true,
      });

      if (!confirmar) return;

      this.justificativaAcao = 'exclusao-documento';
      this.documentoExcluirPayload = {
        ataId: ata.id,
        documentoId: documento.id,
      };
      this.modalJustificativa = true;
    },
    async excluirDocumentoAtaConfirmada(justificativa) {
      const payload = this.documentoExcluirPayload;
      if (!payload?.ataId || !payload?.documentoId) return;

      this.loading = true;
      try {
        await this.removerOfertaCompraAtaPrecoDocumento({
          ataId: payload.ataId,
          documentoId: payload.documentoId,
          justificativa,
        });
        this.$alerta('Documento excluído do PNCP', 'is-success');
        this.fecharJustificativa();
      } catch (error) {
        const msg =
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          'Erro ao excluir documento da ata';
        this.$alerta(msg, 'is-danger');
      } finally {
        this.loading = false;
      }
    },
    async excluirParteEnvolvida({ ata, parte }) {
      if (!ata?.id || !parte?.id) return;

      if (!ata.sequencialAta) {
        this.$alerta(
          'A ata ainda não possui sequencial no PNCP; conclua o envio antes de excluir partes envolvidas.',
          'is-warning',
        );
        return;
      }

      if (Number(parte.tipoParteEnvolvidaId) === TIPO_PARTE_GERENCIADORA) {
        this.$alerta('A gerenciadora da ata não pode ser excluída.', 'is-warning');
        return;
      }

      const confirmar = await this.$confirmacao({
        message:
          'A exclusão remove a parte envolvida no PNCP e no sistema. Esta ação não pode ser desfeita. Deseja continuar?',
        cancelText: 'Cancelar',
        confirmText: 'Continuar',
        type: 'is-danger',
        hasIcon: true,
      });

      if (!confirmar) return;

      this.justificativaAcao = 'exclusao-parte';
      this.parteExcluirPayload = { ataId: ata.id, parteId: parte.id };
      this.modalJustificativa = true;
    },
    async excluirParteEnvolvidaConfirmada(justificativa) {
      const payload = this.parteExcluirPayload;
      if (!payload?.ataId || !payload?.parteId) return;

      this.loading = true;
      try {
        await this.removerOfertaCompraAtaPrecoParteEnvolvida({
          ataId: payload.ataId,
          parteId: payload.parteId,
          justificativa,
        });
        this.$alerta('Parte envolvida excluída do PNCP', 'is-success');
        this.fecharJustificativa();
      } catch (error) {
        const msg =
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          'Erro ao excluir parte envolvida';
        this.$alerta(msg, 'is-danger');
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.arp-hub {
  position: relative;
  min-height: 240px;
  padding: 0.5rem 0 1.5rem;
}

.arp-hub__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.25rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #dbdbdb;
}

.arp-hub__title-row {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  min-width: 0;
}

.arp-hub__tour-help ::v-deep .feature-tour-help__trigger {
  padding: 0.15rem;
}
</style>
